import { z } from 'zod';
import { cnpjValido, normalizarCnpj } from './saas';

// ─────────── Fase 5 · Fiscal, documentação e RENAVE (docs/08) ───────────

const texto = (max: number) => z.string().trim().max(max);
const textoOpc = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const dia = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (AAAA-MM-DD)')
  .refine((v) => !Number.isNaN(new Date(`${v}T00:00:00Z`).getTime()), 'Data inválida');

export const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB',
  'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
] as const; // prettier-ignore
export type Uf = (typeof UFS)[number];

// ─────────── Motor fiscal ───────────

/** Operações com NF-e emitida pela própria loja. */
export const OPERACOES_FISCAIS = ['compra', 'consignacao', 'venda', 'transferencia'] as const;
export type OperacaoFiscal = (typeof OPERACOES_FISCAIS)[number];
export const NOME_OPERACAO: Record<OperacaoFiscal, string> = {
  compra: 'Entrada por compra',
  consignacao: 'Entrada em consignação',
  venda: 'Venda',
  transferencia: 'Transferência entre filiais',
};
/** Entrada (a loja recebe o carro) ou saída (o carro sai da loja). */
export const TIPO_DA_OPERACAO: Record<OperacaoFiscal, 'entrada' | 'saida'> = {
  compra: 'entrada',
  consignacao: 'entrada',
  venda: 'saida',
  transferencia: 'saida',
};

export const REGIMES_TRIBUTARIOS = ['simples_nacional', 'lucro_presumido', 'lucro_real'] as const;
export type RegimeTributario = (typeof REGIMES_TRIBUTARIOS)[number];
export const NOME_REGIME: Record<RegimeTributario, string> = {
  simples_nacional: 'Simples Nacional',
  lucro_presumido: 'Lucro Presumido',
  lucro_real: 'Lucro Real',
};
/** TODO(contador): confirmar o regime padrão das lojas (docs/08). */
export const REGIME_PADRAO: RegimeTributario = 'lucro_presumido';

/** Tributos atuais e os da reforma tributária (IBS e CBS) no mesmo motor. */
export const TRIBUTOS = ['ICMS', 'PIS', 'COFINS', 'IBS', 'CBS'] as const;
export type Tributo = (typeof TRIBUTOS)[number];

/**
 * Base de cálculo: "margem" = valor da operação − custo de aquisição (regime de usados);
 * "valor" = valor total da operação. A redução da base (centésimos de %) vem da regra.
 */
export const BASES_CALCULO = ['margem', 'valor'] as const;
export type BaseCalculo = (typeof BASES_CALCULO)[number];

const percentual = z.number().int().min(0).max(10_000); // centésimos de %: 1800 = 18,00%

export const tributoRegraSchema = z.object({
  tributo: z.enum(TRIBUTOS),
  base: z.enum(BASES_CALCULO),
  /** Redução da base em centésimos de % (ex.: 9500 = base reduzida em 95%) */
  reducaoBase: percentual.default(0),
  /** Alíquota em centésimos de % (1800 = 18,00%) */
  aliquota: percentual,
  cst: texto(4).min(2, 'Informe o CST/CSOSN'),
  /** Só destacado na nota (ex.: IBS/CBS no ano de teste da reforma): não entra no imposto a pagar */
  informativo: z.boolean().default(false),
});
export type TributoRegra = z.infer<typeof tributoRegraSchema>;

export const regraFiscalSchema = z
  .object({
    operacao: z.enum(OPERACOES_FISCAIS),
    regime: z.enum(REGIMES_TRIBUTARIOS),
    /** null = vale para todos os estados; regra do estado tem prioridade */
    uf: z.enum(UFS).nullable().default(null),
    cfop: z.string().regex(/^[1-7]\d{3}$/, 'CFOP com 4 dígitos'),
    /** NCM do veículo na nota (8 dígitos) */
    ncm: z
      .string()
      .regex(/^\d{8}$/, 'NCM com 8 dígitos')
      .nullable()
      .default(null),
    naturezaOperacao: texto(60).min(3),
    tributos: z.array(tributoRegraSchema).max(10),
    vigenciaInicio: dia,
    observacao: textoOpc(500),
  })
  .refine(
    (r) => new Set(r.tributos.map((t) => t.tributo)).size === r.tributos.length,
    'Cada tributo aparece uma vez',
  );
export type RegraFiscalEntrada = z.infer<typeof regraFiscalSchema>;

export interface RegraFiscal extends Omit<RegraFiscalEntrada, 'observacao'> {
  id: string;
  vigenciaFim: string | null;
  observacao?: string | null;
}

/** Regra vigente na data: a do estado ganha da geral; entre iguais, a de início mais recente. */
export function regraVigente<
  R extends {
    operacao: string;
    regime: string;
    uf: string | null;
    vigenciaInicio: string;
    vigenciaFim: string | null;
  },
>(
  regras: readonly R[],
  q: { operacao: OperacaoFiscal; regime: RegimeTributario; uf: string | null; data: string },
): R | null {
  const candidatas = regras.filter(
    (r) =>
      r.operacao === q.operacao &&
      r.regime === q.regime &&
      (r.uf === null || r.uf === q.uf) &&
      r.vigenciaInicio <= q.data &&
      (r.vigenciaFim === null || r.vigenciaFim >= q.data),
  );
  candidatas.sort(
    (a, b) =>
      Number(b.uf !== null) - Number(a.uf !== null) ||
      b.vigenciaInicio.localeCompare(a.vigenciaInicio),
  );
  return candidatas[0] ?? null;
}

export interface TributoCalculado {
  tributo: Tributo;
  cst: string;
  baseCentavos: number;
  aliquota: number;
  valorCentavos: number;
  informativo: boolean;
}

/** Cálculo puro dos tributos de uma nota a partir da regra (sem nada fixo no código). */
export function calcularTributos(
  op: { valorCentavos: number; custoAquisicaoCentavos: number },
  tributos: readonly (Omit<TributoRegra, 'informativo'> & { informativo?: boolean })[],
): { margemCentavos: number; itens: TributoCalculado[]; totalCentavos: number } {
  const margem = Math.max(0, op.valorCentavos - op.custoAquisicaoCentavos);
  const itens = tributos.map((t) => {
    const bruta = t.base === 'margem' ? margem : op.valorCentavos;
    const base = Math.round((bruta * (10_000 - (t.reducaoBase ?? 0))) / 10_000);
    return {
      tributo: t.tributo,
      cst: t.cst,
      baseCentavos: base,
      aliquota: t.aliquota,
      valorCentavos: Math.round((base * t.aliquota) / 10_000),
      informativo: t.informativo ?? false,
    };
  });
  return {
    margemCentavos: margem,
    itens,
    /** Total a pagar (sem os tributos só informativos) */
    totalCentavos: itens.reduce((s, i) => s + (i.informativo ? 0 : i.valorCentavos), 0),
  };
}

export const formatarAliquota = (a: number) =>
  `${(a / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// ─────────── Configuração fiscal da filial ───────────

export const AMBIENTES_NFE = ['homologacao', 'producao'] as const;
export type AmbienteNfe = (typeof AMBIENTES_NFE)[number];

export const configFiscalSchema = z.object({
  cnpj: z.string().refine(cnpjValido, 'CNPJ inválido').transform(normalizarCnpj),
  razaoSocial: texto(120).min(3, 'Informe a razão social'),
  inscricaoEstadual: texto(20).min(2, 'Informe a inscrição estadual (ou ISENTO)'),
  regime: z.enum(REGIMES_TRIBUTARIOS),
  uf: z.enum(UFS),
  municipio: texto(80).min(2),
  codigoMunicipio: z.string().regex(/^\d{7}$/, 'Código IBGE com 7 dígitos'),
  cep: z
    .string()
    .transform((c) => c.replace(/\D/g, ''))
    .refine((c) => c.length === 8, 'CEP com 8 dígitos'),
  logradouro: texto(120).min(2),
  numero: texto(10).min(1),
  bairro: texto(60).min(2),
  serie: z.number().int().min(1).max(999).default(1),
  ambiente: z.enum(AMBIENTES_NFE).default('homologacao'),
  /** Código do estabelecimento na integradora RENAVE (credenciamento próprio da filial) */
  renaveEstabelecimento: textoOpc(30),
  /** Contador da loja: recebe o XML do mês */
  emailContador: z
    .string()
    .trim()
    .toLowerCase()
    .max(200)
    .refine((v) => v === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v), 'E-mail inválido')
    .transform((v) => v || null)
    .nullable()
    .optional(),
});
export type ConfigFiscal = z.infer<typeof configFiscalSchema>;

export const enderecoSchema = z.object({
  cep: z
    .string()
    .transform((c) => c.replace(/\D/g, ''))
    .refine((c) => c.length === 8, 'CEP com 8 dígitos'),
  logradouro: texto(120).min(2, 'Informe a rua'),
  numero: texto(10).min(1, 'Informe o número'),
  complemento: textoOpc(60),
  bairro: texto(60).min(2, 'Informe o bairro'),
  cidade: texto(80).min(2, 'Informe a cidade'),
  uf: z.enum(UFS),
});
export type Endereco = z.infer<typeof enderecoSchema>;

// ─────────── Notas fiscais ───────────

export const STATUS_NOTA = [
  'aguardando_dados',
  'processando',
  'autorizada',
  'rejeitada',
  'cancelada',
] as const;
export type StatusNota = (typeof STATUS_NOTA)[number];
export const NOME_STATUS_NOTA: Record<StatusNota, string> = {
  aguardando_dados: 'Aguardando dados',
  processando: 'Enviando',
  autorizada: 'Autorizada',
  rejeitada: 'Rejeitada',
  cancelada: 'Cancelada',
};

export const emitirNotaSchema = z.object({
  operacao: z.enum(OPERACOES_FISCAIS),
  veiculoId: z.string().uuid(),
  /** Remetente (compra/consignação) ou destinatário (venda). Transferência usa a filial. */
  clienteId: z.string().uuid().optional(),
  /** Venda: valor da nota (padrão: valor da venda) */
  valorCentavos: z.number().int().positive().optional(),
});
export const completarNotaSchema = z.object({ clienteId: z.string().uuid() });
export const cancelarNotaSchema = z.object({
  justificativa: texto(255).min(15, 'A justificativa precisa de pelo menos 15 caracteres'),
});
/** Compra de outra loja: a NF-e é do fornecedor; a loja informa a chave recebida. */
export const notaFornecedorSchema = z.object({
  chave: z
    .string()
    .transform((c) => c.replace(/\D/g, ''))
    .refine((c) => c.length === 44, 'A chave da NF-e tem 44 dígitos'),
});

// ─────────── RENAVE ───────────

export const TIPOS_RENAVE = ['entrada', 'consignacao', 'saida', 'transferencia'] as const;
export type TipoRenave = (typeof TIPOS_RENAVE)[number];
export const NOME_TIPO_RENAVE: Record<TipoRenave, string> = {
  entrada: 'Entrada em estoque',
  consignacao: 'Entrada em consignação',
  saida: 'Saída (venda)',
  transferencia: 'Transferência entre estabelecimentos',
};
export const STATUS_RENAVE = ['pendente', 'processando', 'regular', 'erro'] as const;
export type StatusRenave = (typeof STATUS_RENAVE)[number];

export const CONFORMIDADES = ['regular', 'pendente', 'erro'] as const;
export type Conformidade = (typeof CONFORMIDADES)[number];
export const NOME_CONFORMIDADE: Record<Conformidade, string> = {
  regular: 'Regular',
  pendente: 'Pendente',
  erro: 'Com erro',
};

/** Conformidade do carro: erro em qualquer registro > algo pendente > tudo regular. */
export function conformidadeDoVeiculo(status: readonly StatusRenave[]): Conformidade {
  if (status.includes('erro')) return 'erro';
  if (!status.length || status.some((s) => s !== 'regular')) return 'pendente';
  return 'regular';
}

// ─────────── Prazos e alertas ───────────

/**
 * Prazos legais usados nos alertas. TODO(despachante/contador): conferir antes de produção.
 * comunicacaoVenda: CTB art. 134 (vendedor comunica a venda); transferenciaComprador: CTB art. 123 §1º.
 */
export const PRAZOS_DOCUMENTAIS = {
  comunicacaoVendaDias: 60,
  transferenciaCompradorDias: 30,
  /** Avisos antes do vencimento do certificado A1 */
  avisosCertificadoDias: [30, 15, 7, 1] as readonly number[],
  /** Contrato de consignação: aviso quando o prazo combinado acaba */
  consignacaoPadraoDias: 90,
};

export const TIPOS_ALERTA_DOCUMENTAL = [
  'certificado_vencendo',
  'certificado_vencido',
  'certificado_ausente',
  'nota_rejeitada',
  'renave_erro',
  'renave_pendente',
  'comunicacao_venda',
  'transferencia_comprador',
  'debitos',
  'vistoria',
  'contrato_pendente',
] as const;
export type TipoAlertaDocumental = (typeof TIPOS_ALERTA_DOCUMENTAL)[number];

export interface AlertaDocumental {
  tipo: TipoAlertaDocumental;
  gravidade: 'alta' | 'media' | 'baixa';
  titulo: string;
  detalhe: string;
  link: string | null;
  veiculoId: string | null;
  filialId: string | null;
  prazo: string | null;
}

/** Dias corridos entre duas datas AAAA-MM-DD (b − a). */
export function diasEntre(a: string, b: string): number {
  return Math.round(
    (new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86_400_000,
  );
}

// ─────────── Documentos ───────────

export const TIPOS_DOCUMENTO = [
  'crlv',
  'atpv',
  'nfe',
  'contrato',
  'laudo',
  'vistoria',
  'comprovante',
  'documento_pessoal',
  'procuracao',
  'outros',
  'cnh',
  'comprovante_residencia',
] as const;
export type TipoDocumento = (typeof TIPOS_DOCUMENTO)[number];
export const NOME_TIPO_DOCUMENTO: Record<TipoDocumento, string> = {
  crlv: 'CRLV',
  atpv: 'ATPV-e',
  nfe: 'Nota fiscal',
  contrato: 'Contrato',
  laudo: 'Laudo cautelar',
  vistoria: 'Vistoria',
  comprovante: 'Comprovante',
  documento_pessoal: 'Documento pessoal',
  procuracao: 'Procuração',
  outros: 'Outros',
  cnh: 'CNH',
  comprovante_residencia: 'Comprovante de residência',
};
export const MAX_BYTES_DOCUMENTO = 10 * 1024 * 1024;

export const documentoEnviarSchema = z
  .object({
    tipo: z.enum(TIPOS_DOCUMENTO),
    veiculoId: z.string().uuid().optional(),
    clienteId: z.string().uuid().optional(),
    descricao: textoOpc(120),
  })
  .refine((d) => d.veiculoId || d.clienteId, 'Informe o carro ou o cliente');

// ─────────── Contratos e assinatura ───────────

export const TIPOS_CONTRATO = ['compra_venda', 'compra', 'consignacao', 'garantia'] as const;
export type TipoContrato = (typeof TIPOS_CONTRATO)[number];
export const NOME_TIPO_CONTRATO: Record<TipoContrato, string> = {
  compra_venda: 'Compra e venda (loja vende)',
  compra: 'Compra (loja compra)',
  consignacao: 'Consignação',
  garantia: 'Termo de garantia',
};
export const STATUS_CONTRATO = [
  'rascunho',
  'aguardando_assinatura',
  'assinado',
  'recusado',
  'cancelado',
] as const;
export type StatusContrato = (typeof STATUS_CONTRATO)[number];
export const NOME_STATUS_CONTRATO: Record<StatusContrato, string> = {
  rascunho: 'Rascunho',
  aguardando_assinatura: 'Aguardando assinatura',
  assinado: 'Assinado',
  recusado: 'Recusado',
  cancelado: 'Cancelado',
};

export const contratoCriarSchema = z.object({
  tipo: z.enum(TIPOS_CONTRATO),
  veiculoId: z.string().uuid(),
  clienteId: z.string().uuid(),
  /** Consignação: prazo e valor combinado com o consignante */
  prazoDias: z.number().int().min(1).max(365).optional(),
  /** Garantia: meses de garantia da loja */
  garantiaMeses: z.number().int().min(1).max(36).optional(),
});
export const modeloContratoSchema = z.object({
  tipo: z.enum(TIPOS_CONTRATO),
  titulo: texto(120).min(3),
  corpo: texto(30_000).min(20),
});

/** Campos que os modelos de contrato podem usar ({{campo}}). */
export const CAMPOS_CONTRATO = [
  'loja.razao_social',
  'loja.cnpj',
  'loja.endereco',
  'loja.cidade',
  'cliente.nome',
  'cliente.cpf',
  'cliente.endereco',
  'veiculo.descricao',
  'veiculo.placa',
  'veiculo.chassi',
  'veiculo.renavam',
  'veiculo.ano',
  'veiculo.km',
  'valor',
  'valor_extenso',
  'forma_pagamento',
  'prazo_dias',
  'garantia_meses',
  'garantia_km',
  'garantia',
  'data',
] as const;

/** Troca {{campo}} pelos valores; campo desconhecido vira "____" para a loja perceber. */
export function preencherModelo(corpo: string, valores: Record<string, string>): string {
  return corpo.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k: string) => valores[k] ?? '________');
}

/** Valor em reais por extenso (até 999 milhões), para contratos. */
export function reaisPorExtenso(centavos: number): string {
  const un = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
  const dez = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove']; // prettier-ignore
  const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa']; // prettier-ignore
  const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos']; // prettier-ignore
  const ate999 = (n: number): string => {
    if (n === 0) return '';
    if (n === 100) return 'cem';
    const c = Math.floor(n / 100);
    const r = n % 100;
    const partes: string[] = [];
    if (c) partes.push(centenas[c]!);
    if (r >= 10 && r < 20) partes.push(dez[r - 10]!);
    else {
      const d = Math.floor(r / 10);
      const u = r % 10;
      if (d) partes.push(dezenas[d]!);
      if (u) partes.push(un[u]!);
    }
    return partes.join(' e ');
  };
  const inteiro = Math.floor(Math.abs(centavos) / 100);
  const cent = Math.abs(centavos) % 100;
  const milhoes = Math.floor(inteiro / 1_000_000);
  const milhares = Math.floor((inteiro % 1_000_000) / 1000);
  const resto = inteiro % 1000;
  const grupos: { t: string; v: number }[] = [];
  if (milhoes) grupos.push({ t: `${ate999(milhoes)} ${milhoes === 1 ? 'milhão' : 'milhões'}`, v: milhoes }); // prettier-ignore
  if (milhares) grupos.push({ t: milhares === 1 ? 'mil' : `${ate999(milhares)} mil`, v: milhares });
  if (resto) grupos.push({ t: ate999(resto), v: resto });
  let reais = '';
  if (grupos.length) {
    const ult = grupos.at(-1)!;
    const sep = grupos.length > 1 && (ult.v < 100 || ult.v % 100 === 0) ? ' e ' : ' ';
    reais = grupos.length > 1 ? `${grupos.slice(0, -1).map((g) => g.t).join(' ')}${sep}${ult.t}` : ult.t; // prettier-ignore
    const redondo = !resto && !milhares && milhoes;
    reais += `${redondo ? ' de' : ''} ${inteiro === 1 ? 'real' : 'reais'}`;
  }
  const c = cent ? `${ate999(cent)} ${cent === 1 ? 'centavo' : 'centavos'}` : '';
  if (reais && c) return `${reais} e ${c}`;
  return reais || c || 'zero reais';
}

// ─────────── Carta de correção, inutilização, notas recebidas e XML do contador ───────────

/** Carta de correção (CC-e): não pode mudar valores, impostos, quem compra/vende nem a data. */
export const cartaCorrecaoSchema = z.object({
  texto: z.string().trim().min(15, 'Descreva a correção (pelo menos 15 letras)').max(1000),
});
export const inutilizacaoSchema = z
  .object({
    filialId: z.string().uuid(),
    serie: z.number().int().min(1).max(999),
    numeroInicial: z.number().int().min(1).max(999_999_999),
    numeroFinal: z.number().int().min(1).max(999_999_999),
    justificativa: z.string().trim().min(15, 'Motivo com pelo menos 15 letras').max(255),
  })
  .refine((d) => d.numeroFinal >= d.numeroInicial, {
    message: 'O número final deve ser igual ou maior que o inicial',
    path: ['numeroFinal'],
  })
  .refine((d) => d.numeroFinal - d.numeroInicial < 1000, {
    message: 'Até 1.000 números por vez',
    path: ['numeroFinal'],
  });
export type Inutilizacao = z.infer<typeof inutilizacaoSchema>;

export const MANIFESTACOES = [
  'ciencia',
  'confirmacao',
  'desconhecimento',
  'nao_realizada',
] as const;
export type Manifestacao = (typeof MANIFESTACOES)[number];
export const NOME_MANIFESTACAO: Record<Manifestacao, string> = {
  ciencia: 'Ciência da operação',
  confirmacao: 'Confirmar a operação',
  desconhecimento: 'Desconheço esta nota',
  nao_realizada: 'Operação não realizada',
};
export const manifestacaoSchema = z
  .object({
    tipo: z.enum(MANIFESTACOES),
    justificativa: z.string().trim().max(255).optional(),
  })
  .refine((d) => d.tipo !== 'nao_realizada' || (d.justificativa?.length ?? 0) >= 15, {
    message: 'Explique por que a operação não aconteceu (pelo menos 15 letras)',
    path: ['justificativa'],
  });

export const exportarXmlSchema = z.object({
  mes: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mês no formato AAAA-MM'),
  filialId: z.string().uuid().optional(),
});
export const enviarContadorSchema = exportarXmlSchema.extend({
  para: z.string().trim().toLowerCase().email('E-mail inválido').max(200),
});
